import React, { useMemo } from 'react';
import Heartbeats, {
	Breadcrumbs,
	SupportUrlKeys,
	type SupportUrls,
	HeartbeatsTour,
} from '@atlassian/eoc-heartbeats';
import { heartbeatsResource } from '@atlassian/eoc-heartbeats/resources';
import { IPHStoreContainer } from '@atlassian/eoc-iph-store';
import { sidebarRightsResource, teamResource } from '@atlassian/eoc-team-dashboard/resources';
import Spinner from '@atlassian/jira-common-components-spinner/src/view.tsx';
import CoordinationClient from '@atlassian/jira-engagement/src/ui/coordination-client/index.tsx';
import { GlobalContextContainer } from '@atlassian/jira-eoc-global-context/src/index.tsx';
import { useOgTenantInfo } from '@atlassian/jira-eoc-tenant-info/src/controllers/index.tsx';
import { ConsolidatedStateWrapper } from '@atlassian/jira-eoc-tenant-info/src/ui/index.tsx';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/JSErrorBoundary.tsx';
import { GenericPermissionErrorPageAsync } from '@atlassian/jira-error-pages/src/async.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { DocumentTitle } from '@atlassian/jira-global-document-title/src/DocumentTitle.tsx';
import { useHelpPanelLink } from '@atlassian/jira-help-panel-button/src/index.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { useOpsgenieBaseUrl } from '@atlassian/jira-platform-get-og-base-url/src/main.tsx';
import { AuthenticationCheck } from '@atlassian/jira-spa-apps-common/src/interceptors/authentication/index.tsx';
import { SpaStatePageReady } from '@atlassian/jira-spa-state-controller/src/components/main.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { useIsAdmin } from '@atlassian/jira-tenant-context-controller/src/components/is-admin/index.tsx';
import { useResource, usePathParam } from '@atlassian/react-resource-router';
import { useFeatures } from '@atlassian/jira-navigation-apps-sidebar-global-settings-products/src/controllers/features/index.tsx';
import { heartbeatsLinks } from './constants.tsx';
import messages from './messages.tsx';

const HeartbeatsPage = () => {
	const { formatMessage } = useIntl();
	const [teamId] = usePathParam('teamId');
	const cloudId = useCloudId();
	const helpPanelLink = useHelpPanelLink();
	const [{ fetchStatus, opsgenieBaseUrl }] = useOpsgenieBaseUrl('eocHeartbeats');
	const opsgenieGlobalHeartbeatsUrl =
		fetchStatus === 'SUCCESS' ? `${opsgenieBaseUrl}/settings/heartbeat` : '';
	const isPrimaryAdmin = useIsAdmin();
	const { isTransitioning } = useOgTenantInfo();
	const { data: features } = useFeatures();
	const { data: sidebarRights, loading: sidebarRightsLoading } = useResource(sidebarRightsResource);
	const { data: team } = useResource(teamResource);
	const { data } = useResource(heartbeatsResource);
	const hasAnyHeartbeat = useMemo(() => data?.heartbeats.length !== 0, [data]);
	const canDisplayTeamHeartbeats = Boolean(sidebarRights?.showHeartbeats);

	if (team && fg('enable-sidebar-rights-check')) {
		if (sidebarRightsLoading) {
			return <Spinner />;
		}
		if (!canDisplayTeamHeartbeats) {
			return <GenericPermissionErrorPageAsync />;
		}
	}

	if (!team && features?.hasGlobalHeartbeatsAccess === false && fg('jira-roles-check-in-view')) {
		return <GenericPermissionErrorPageAsync />;
	}

	// TODO: Remove this if after cleaning up global hb flag.
	if (!team && !fg('global_heartbeats')) {
		return <GenericPermissionErrorPageAsync />;
	}

	const supportUrls: SupportUrls = {
		[SupportUrlKeys.API_DOCUMENTATION]:
			'https://developer.atlassian.com/cloud/jira/service-desk-ops/rest/v2/api-group-heartbeats/#api-group-heartbeats',
		[SupportUrlKeys.OG_GLOBAL_HEARTBEATS]: opsgenieGlobalHeartbeatsUrl,
		[SupportUrlKeys.API_PING_URL]: `/jsm/ops/api/${cloudId}/v1/teams/${teamId}/heartbeats/ping`,
		[SupportUrlKeys.HEARTBEATS_API]:
			'https://support.atlassian.com/atlassian-account/docs/manage-api-tokens-for-your-atlassian-account',
		[SupportUrlKeys.CREATE_API_TOKEN]:
			'https://developer.atlassian.com/cloud/jira/service-desk/basic-auth-for-rest-apis/#basic-auth-for-rest-apis',
		[SupportUrlKeys.EMAIL_INSTRUCTIONS]:
			'https://support.atlassian.com/jira-service-management-cloud/docs/create-a-heartbeat-with-built-in-email',
	};

	return (
		<JSErrorBoundary teamName="alexstrasza" packageName="jiraEocHeartbeats" id="jiraHeartbeatsPage">
			<AuthenticationCheck>
				<DocumentTitle title={formatMessage(messages.pageTitle)} />
				<IPHStoreContainer clickHandler={helpPanelLink} links={heartbeatsLinks}>
					<GlobalContextContainer teamName="alexstrasza" packageName="jiraEocHeartbeats">
						<ConsolidatedStateWrapper>
							<Heartbeats
								isTransitioning={isTransitioning}
								isPrimaryAdmin={isPrimaryAdmin}
								supportUrls={supportUrls}
								breadcrumbs={<Breadcrumbs />}
							/>
							<SpaStatePageReady />
							{hasAnyHeartbeat && (
								<CoordinationClient messageId="jira-ops_team_heartbeats" messageType="engagement">
									<HeartbeatsTour learnMoreUrl="https://support.atlassian.com/jira-service-management-cloud/docs/what-are-heartbeats/" />
								</CoordinationClient>
							)}
						</ConsolidatedStateWrapper>
					</GlobalContextContainer>
				</IPHStoreContainer>
			</AuthenticationCheck>
		</JSErrorBoundary>
	);
};

export default HeartbeatsPage;
